// EP-161 §2.3, S6 (T161-W02a): after "เช็กเอาต์" at the gate, the staff
// member's phone shows the charge and the PromptPay QR for the driver to
// scan, then takes cash or verifies the transfer (§10 item 30).
import { useTranslation } from 'react-i18next'
import { QRCodeSVG } from 'qrcode.react'
import { BottomSheet, Button } from '@/components/ui'
import { useDateTimeFormatter } from '@/hooks/useDateFormatter'
import { formatBaht } from '@/lib/money'
import type { ParkingSession } from '@/types/parking'

// An EMVCo payload is at least this long; a shorter one draws no QR (the
// kiosk PayPage keeps the same guard). The same qrcode.react as the kiosk,
// drawn as SVG so it stays sharp on a phone held up to the driver.
const MIN_PAYLOAD_LENGTH = 20

export interface GateCheckOutSheetProps {
  /** The row the check-out answered, with its new pending payment. */
  session: ParkingSession
  onClose: () => void
  onTakeCash: () => void
  onVerify: () => void
}

// From the server's entry and exit times; a check-out always sets exited_at.
function useDurationText(enteredAt: string, exitedAt: string | null): string | null {
  const { t } = useTranslation('parking')
  if (!exitedAt) return null
  const minutes = Math.max(0, Math.floor((new Date(exitedAt).getTime() - new Date(enteredAt).getTime()) / 60_000))
  const hours = Math.floor(minutes / 60)
  return hours > 0
    ? t('sessions.gate.duration_hours_minutes', { hours, minutes: minutes % 60 })
    : t('sessions.gate.duration_minutes', { minutes })
}

export function GateCheckOutSheet({ session, onClose, onTakeCash, onVerify }: GateCheckOutSheetProps) {
  const { t } = useTranslation('parking')
  const formatDateTime = useDateTimeFormatter()
  const duration = useDurationText(session.entered_at, session.exited_at)
  const payment = session.payment
  const charge = payment?.amount ?? session.charge_amount
  const payload = payment?.promptpay_payload ?? ''
  const hasQr = payload.trim().length >= MIN_PAYLOAD_LENGTH
  const title = t('sessions.gate.sheet_title', { plate: session.license_plate })

  return (
    <BottomSheet isOpen onClose={onClose} title={title}>
      <div className="space-y-4 px-2" data-testid="gate-check-out-sheet">
        <div className="text-sm space-y-0.5">
          {session.zone_name && <div className="text-base-content/70">{session.zone_name}</div>}
          <div>
            {t('sessions.gate.entered_at', { time: formatDateTime(session.entered_at) })}
            {duration && ` · ${t('sessions.gate.duration', { duration })}`}
          </div>
          <div className="text-lg font-semibold" data-testid="gate-charge">
            {t('sessions.gate.charge', { amount: charge == null ? '-' : formatBaht(Number(charge)) })}
          </div>
        </div>
        {hasQr ? (
          <div className="text-center space-y-2">
            <QRCodeSVG
              value={payload}
              size={192}
              className="w-48 h-48 mx-auto"
              aria-label={t('sessions.gate.qr_label')}
              data-testid="gate-qr"
            />
            <p className="text-sm">{t('sessions.gate.scan_to_pay')}</p>
            {payment?.promptpay_ref1 && (
              <p className="text-xs font-mono text-base-content/60">{payment.promptpay_ref1}</p>
            )}
          </div>
        ) : (
          <p className="text-sm text-base-content/70">{t('sessions.gate.no_qr')}</p>
        )}
        <div className="flex flex-wrap gap-2">
          <Button onClick={onTakeCash}>{t('sessions.gate.take_cash')}</Button>
          {payment && (
            <Button variant="success" onClick={onVerify}>
              {t('sessions.gate.verify')}
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>
            {t('sessions.gate.close')}
          </Button>
        </div>
      </div>
    </BottomSheet>
  )
}
